import { Link } from "react-router-dom";

export default function VaLoanBenefitsExplained() {
    return (
        <>
            <p>
                If you've served in the military, a VA loan is one of the most valuable financial benefits you have access to.
                It's not marketed aggressively because it doesn't generate commissions for most lenders the way other products
                do. Here's what you actually get and what the numbers look like compared to conventional financing.
            </p>

            <h2>The Core Benefits</h2>
            <p>
                <strong>No down payment required.</strong> This is the headline feature. Most conventional loans require at
                least 3%–5% down, and many buyers put 10–20% down. On a $350,000 home, that's $10,500 to $70,000 upfront.
                VA loans have no minimum down payment requirement. You can finance 100% of the purchase price.
            </p>
            <p>
                <strong>No private mortgage insurance (PMI).</strong> Conventional loans with less than 20% down require PMI —
                typically $100–$300/month. FHA loans require mortgage insurance for the life of the loan. VA loans have neither.
                On a $300,000 loan, that's $150–$250/month you don't pay, every month, until you'd otherwise reach 20% equity.
            </p>
            <p>
                <strong>Competitive interest rates.</strong> VA loans typically come with lower interest rates than conventional
                loans, often 0.25%–0.5% lower, because the VA guarantee reduces lender risk. On a $300,000 30-year loan,
                a 0.5% rate advantage saves roughly $90/month — $32,400 over the life of the loan.
            </p>
            <p>
                <strong>Limits on closing costs.</strong> The VA limits what lenders can charge. Certain fees are prohibited,
                including the 1% origination fee cap and restrictions on junk fees. Sellers are also permitted to pay up to 4%
                of the purchase price in concessions, including VA closing costs.
            </p>
            <p>
                Use the <Link to="/calculator/va-loan">VA loan calculator</Link> to model your specific purchase price,
                funding fee, and monthly payment compared to a conventional loan.
            </p>

            <h2>The VA Funding Fee</h2>
            <p>
                This is the catch — though it's smaller than most people expect and can be financed into the loan.
            </p>
            <p>
                First-time VA loan use, no down payment: 2.15% of the loan amount.<br />
                First-time use, 5–9.99% down: 1.5%.<br />
                First-time use, 10%+ down: 1.25%.<br />
                Subsequent use, no down payment: 3.3%.
            </p>
            <p>
                On a $300,000 loan with no down payment and first-time use, the funding fee is $6,450. That gets added to your
                loan balance, so you'd be borrowing $306,450. You're paying interest on it, but you avoided $45,000 in down
                payment and you're saving $150–$200/month on PMI.
            </p>
            <p>
                <strong>Exemptions:</strong> Veterans receiving VA disability compensation, surviving spouses of veterans who
                died in service or from a service-connected disability, and a few other categories are exempt from the funding
                fee entirely. If you have a service-connected disability rating, check your eligibility before closing.
            </p>

            <h2>Who Qualifies</h2>
            <p>
                Eligibility is based on service history. Generally:
            </p>
            <p>
                <strong>Active duty:</strong> 90 continuous days of service.<br />
                <strong>Veterans (wartime):</strong> 90 days active duty with any part during wartime.<br />
                <strong>Veterans (peacetime):</strong> 181 days of continuous active service.<br />
                <strong>National Guard and Reserves:</strong> 6 years of service, or 90 days of active-duty service.<br />
                <strong>Surviving spouses:</strong> may also qualify in certain cases.
            </p>
            <p>
                You'll need a Certificate of Eligibility (COE) — your lender can pull this for you in minutes via the
                VA's online system, or you can request one yourself on eBenefits.
            </p>

            <h2>Is there a loan limit?</h2>
            <p>
                As of 2020, eligible borrowers with full entitlement have no VA loan limit. The cap was effectively
                removed for first-time use with full entitlement. Lenders still apply their own underwriting standards
                based on income, credit, and debt-to-income ratio, but the VA itself doesn't cap how much you can borrow.
            </p>

            <h2>The bottom line</h2>
            <p>
                For eligible borrowers, a VA loan is almost always the financially superior choice over conventional or
                FHA. Zero down, no PMI, lower rates, and capped closing costs add up to tens of thousands in savings
                over the life of the loan. The one trade-off — the funding fee — is small relative to those benefits,
                and many disabled veterans are exempt entirely.
            </p>

            <p>
                Run a side-by-side with our <Link to="/calculator/va-loan">VA loan calculator</Link> and{" "}
                <Link to="/calculator/mortgage">conventional mortgage calculator</Link> to see exactly how much you'd
                save on your specific purchase.
            </p>
        </>
    );
}